import { useCallback, useEffect, useMemo, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { PlusIcon, X } from "lucide-react";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "./VideoPlayerV3";
import { getHashAndThumbnailsForVideos } from "@/utils/getHashandthumbnail";
import toast from "react-hot-toast";

/**
 * Props interface for the VideoUploaderWithPreview component
 * Provides flexible configuration for different use cases
 */
interface FileUploadProps {
  videoId?: number; // Optional video ID for tracking
  onFilesUploaded?: (files: FileDetails[]) => void; // Callback when files are successfully uploaded
  maxFiles?: number; // Maximum number of files allowed
  defaultValue?: FileDetails[]; // Initial files to display
  maxSize?: number; // Maximum file size in bytes
  accept?: { [key: string]: string[] }; // Accepted file types
  clearFiles?: boolean; // Trigger to clear all files
  showSelectedFiles?: boolean; // Whether to show uploaded files
  useExternalState?: boolean; // Use external state management instead of internal
  externalFiles?: FileDetails[]; // External files array
  useLiveUrl?: boolean; // Whether to use live URLs
  cardDesignSize?: string; // CSS class for card sizing
  height: string; // Card height
  showUploadAnimationOnly?: boolean; // Show only upload animation
  width: string; // Card width
  setExternalFiles?: React.Dispatch<React.SetStateAction<FileDetails[]>>; // External state setter
  setVideosAreUpload?: React.Dispatch<React.SetStateAction<string | null>>; // Upload status setter
}

/**
 * VideoUploaderWithPreview Component
 *
 * A comprehensive video upload component with the following features:
 * - Drag & drop interface
 * - Multiple file upload support
 * - Real-time upload progress
 * - Video preview with thumbnail generation
 * - Error handling and retry mechanisms
 * - External/internal state management options
 * - Memory management for URL objects
 *
 * Component Flow:
 * 1. User drops/selects files → onDrop triggered
 * 2. Files are immediately added to UI with loading state
 * 3. Each file is processed individually (upload → thumbnail generation)
 * 4. UI updates as each file completes or fails
 * 5. Final callback with successful uploads
 */
const VideoUploaderWithPreview: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  maxFiles = 10,
  showUploadAnimationOnly = false,
  useLiveUrl = false,
  cardDesignSize = "size-[124px]",
  height,
  width,
  showSelectedFiles = true,
  defaultValue = [],
  useExternalState = false,
  externalFiles = [],
  setExternalFiles,
  setVideosAreUpload,
  maxSize = 100 * 1024 * 1024, // 100MB default
  accept = {
    "video/mp4": [],
    "image/gif": [],
  },
  clearFiles = false,
}) => {
  // ==================== STATE MANAGEMENT ====================

  /** Current playing video URL - only one video can play at a time */
  const [playingVideoUrl, setPlayingVideoUrl] = useState("");

  /** Internal files state - used when not using external state management */
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(defaultValue);

  /** Global uploading state - prevents multiple simultaneous uploads */
  const [uploading, setUploading] = useState<boolean>(false);

  /**
   * Upload progress tracking - maps filename to current step
   * e.g., { "video1.mp4": "Uploading video...", "video2.mp4": "Generating thumbnail..." }
   */
  const [uploadSteps, setUploadSteps] = useState<Record<string, string>>({});

  /**
   * Error tracking - maps filename to error message
   * Allows showing specific error messages for failed uploads
   */
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ==================== MEMOIZED VALUES ====================

  /**
   * Determines which files array to use based on useExternalState prop
   * This allows the component to work with both internal and external state management
   */
  const files = useMemo(
    () => (useExternalState ? externalFiles : localFiles),
    [useExternalState, externalFiles, localFiles],
  );

  /**
   * Determines which setter function to use for files
   * Provides a unified interface regardless of state management approach
   */
  const setFiles = useMemo(
    () => (useExternalState ? setExternalFiles : setLocalFiles),
    [useExternalState, setExternalFiles, setLocalFiles],
  );

  /**
   * Memoized card dimensions to prevent unnecessary re-renders
   * Used consistently across all file cards
   */
  const cardDimensions = useMemo(
    () => ({
      width: width || "124px",
      height: height || "124px",
    }),
    [width, height],
  );

  // ==================== EFFECTS ====================

  /**
   * Handle external clearFiles trigger
   * Clears all files, upload steps, and errors when clearFiles prop becomes true
   */
  useEffect(() => {
    if (clearFiles && setFiles) {
      setFiles([]);
      setUploadSteps({});
      setErrors({});
    }
  }, [clearFiles, setFiles]);

  /**
   * Cleanup effect - revokes object URLs to prevent memory leaks
   * Object URLs created with URL.createObjectURL must be manually cleaned up
   */
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.local_url) {
          URL.revokeObjectURL(file.local_url);
        }
      });
    };
  }, [files]);

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Sanitizes filename by removing special characters and limiting length
   * Prevents issues with file storage and ensures valid filenames
   *
   * @param name - Original filename
   * @returns Sanitized filename safe for storage
   */
  const sanitizeFileName = useCallback((name: string): string => {
    return name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100);
  }, []);

  /**
   * Generates a unique filename using timestamp
   * Prevents filename conflicts when multiple files have the same name
   *
   * @param originalName - Original filename
   * @returns Unique filename with timestamp prefix
   */
  const generateUniqueFileName = useCallback(
    (originalName: string): string => {
      const timeStamp = Date.now();
      const sanitized = sanitizeFileName(originalName);
      return `${sanitized}-${timeStamp}`;
    },
    [sanitizeFileName],
  );

  // ==================== UPLOAD STEP MANAGEMENT ====================

  /**
   * Updates the upload progress step for a specific file
   * Used to show real-time progress feedback to users
   *
   * @param filename - Name of the file being uploaded
   * @param step - Current step description (e.g., "Uploading video...")
   */
  const updateUploadStep = useCallback((filename: string, step: string) => {
    setUploadSteps((prev) => ({ ...prev, [filename]: step }));
  }, []);

  /**
   * Removes upload step tracking for a completed file
   * Cleans up the uploadSteps state to prevent memory buildup
   *
   * @param filename - Name of the file to remove from tracking
   */
  const clearUploadedStep = useCallback((filename: string) => {
    setUploadSteps((prev) => {
      const updated = { ...prev };
      delete updated[filename];
      return updated;
    });
  }, []);

  // ==================== ERROR MANAGEMENT ====================

  /**
   * Sets an error message for a specific file
   * Allows displaying specific error feedback to users
   *
   * @param fileName - Name of the file that failed
   * @param error - Error message to display
   */
  const setFileError = useCallback((fileName: string, error: string) => {
    setErrors((prev) => ({ ...prev, [fileName]: error }));
  }, []);

  /**
   * Clears error for a specific file
   * Used when retrying uploads or removing failed files
   *
   * @param fileName - Name of the file to clear error for
   */
  const clearFileError = useCallback((fileName: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
  }, []);

  // ==================== CORE UPLOAD LOGIC ====================

  /**
   * Processes a single file upload with complete error handling
   *
   * Upload Process:
   * 1. Create FileDetails object with local preview URL
   * 2. Upload file to S3 storage
   * 3. Generate video thumbnail and hash
   * 4. Return completed file details
   *
   * @param file - File object to upload
   * @returns Promise<FileDetails> - Completed file details with live URL
   * @throws Error if upload or thumbnail generation fails
   */
  const processFileUpload = useCallback(
    async (file: File): Promise<FileDetails> => {
      const uniqueFileName = generateUniqueFileName(file.name);

      // Create initial file details with local preview
      const fileDetails: FileDetails = {
        name: uniqueFileName,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file), // Create local preview URL
        live_url: null,
        thumbnail: null,
        hash: null,
      };

      try {
        // Step 1: Notify parent component about upload start
        setVideosAreUpload?.(fileDetails.name);
        updateUploadStep(fileDetails.name, "Uploading video...");
        clearFileError(fileDetails.name);

        // Step 2: Upload file to S3
        const uploadedFile = await UploadFileToS3(fileDetails);

        // Step 3: Generate thumbnail and hash
        updateUploadStep(fileDetails.name, "Generating thumbnail...");
        const hashAndThumbnail = await getHashAndThumbnailsForVideos({
          videoUrls: [uploadedFile.live_url as string],
        });

        // Step 4: Create completed file object
        const completedFile: FileDetails = {
          ...fileDetails,
          live_url: uploadedFile.live_url,
          hash: hashAndThumbnail[0]?.hash,
          thumbnail: hashAndThumbnail[0]?.thumbnail,
        };

        // Step 5: Cleanup upload tracking
        clearUploadedStep(fileDetails.name);
        setVideosAreUpload?.(null);

        return completedFile;
      } catch (error) {
        // Cleanup on error
        clearUploadedStep(fileDetails.name);
        setVideosAreUpload?.(null);

        // Set user-friendly error message
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setFileError(fileDetails.name, errorMessage);
        console.error(`Failed to upload file: ${file.name}`, error);

        throw error; // Re-throw to handle in calling function
      }
    },
    [
      generateUniqueFileName,
      setVideosAreUpload,
      updateUploadStep,
      clearUploadedStep,
      clearFileError,
      setFileError,
    ],
  );

  /**
   * Handles file drop event from react-dropzone
   *
   * Drop Process:
   * 1. Validate available slots
   * 2. Add files to UI immediately with loading state
   * 3. Process uploads in parallel
   * 4. Update UI as each upload completes/fails
   * 5. Call onFilesUploaded with successful uploads
   *
   * @param acceptedFiles - Files accepted by dropzone validation
   */
  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[]) => {
      // Prevent multiple simultaneous uploads
      if (uploading || !setFiles) return;

      // Check available slots
      const availableSlots = maxFiles - files.length;
      if (availableSlots <= 0) return;

      // Limit files to available slots
      const filesToProcess = acceptedFiles.slice(0, availableSlots);
      setUploading(true);

      // Create pending file objects for immediate UI feedback
      const pendingFiles = filesToProcess.map((file) => ({
        name: generateUniqueFileName(file.name),
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
        thumbnail: null,
        hash: null,
        uploading: true, // Flag to show loading state
      }));

      // Add pending files to state for immediate UI update
      setFiles((prevFiles) => [...prevFiles, ...pendingFiles]);

      // Process uploads in parallel
      const uploadPromises = filesToProcess.map(async (file, index) => {
        try {
          const completedFile = await processFileUpload(file);

          // Update specific file in state with completed data
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.name === pendingFiles[index].name ? completedFile : f,
            ),
          );

          return completedFile;
        } catch (error) {
          // Remove failed file from state
          setFiles((prevFiles) =>
            prevFiles.filter((f) => f.name !== pendingFiles[index].name),
          );
          return null;
        }
      });

      try {
        // Wait for all uploads to complete
        const results = await Promise.allSettled(uploadPromises);

        // Filter successful uploads
        const successfulUploads = results
          .filter(
            (result): result is PromiseFulfilledResult<FileDetails> =>
              result.status === "fulfilled" && result.value !== null,
          )
          .map((result) => result.value);

        // Notify parent component of successful uploads
        onFilesUploaded?.(successfulUploads);
      } finally {
        setUploading(false);
      }
    },
    [
      uploading,
      setFiles,
      maxFiles,
      files.length,
      generateUniqueFileName,
      processFileUpload,
      onFilesUploaded,
    ],
  );

  // ==================== USER INTERACTION HANDLERS ====================

  /**
   * Handles removing a file from the upload list
   *
   * Removal Process:
   * 1. Revoke object URL to prevent memory leaks
   * 2. Clear upload steps and errors
   * 3. Remove from files array
   * 4. Notify parent component
   *
   * @param index - Index of file to remove
   */
  const handleRemoveFile = useCallback(
    (index: number) => {
      if (!setFiles) return;

      const fileToRemove = files[index];

      // Clean up object URL to prevent memory leaks
      if (fileToRemove.local_url) {
        URL.revokeObjectURL(fileToRemove.local_url);
      }

      // Clear tracking data
      clearUploadedStep(fileToRemove.name);
      clearFileError(fileToRemove.name);

      // Remove from files array
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      onFilesUploaded?.(updatedFiles);
    },
    [setFiles, files, clearUploadedStep, clearFileError, onFilesUploaded],
  );

  /**
   * Handles video play/pause toggle
   * Only one video can play at a time - clicking same video pauses it,
   * clicking different video switches playback
   *
   * @param url - URL of video to play/pause
   */
  const handlePlayVideo = useCallback((url: string) => {
    setPlayingVideoUrl((current) => (current === url ? "" : url));
  }, []);

  // ==================== DROPZONE CONFIGURATION ====================

  /**
   * React-dropzone configuration
   * Handles file validation, drag states, and drop events
   */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, // Handle file drop
    multiple: true, // Allow multiple files
    maxSize, // File size limit
    accept, // Accepted file types
    disabled: uploading, // Disable during upload
    onError: (err: any) => {
      console.log(err);

      if (err?.errors?.length > 0) {
        const message = err.errors[0].message;

        if (message.includes("File is larger")) {
          toast.error("❌ File too large! Max size is 100MB");
        } else if (message.includes("File type must be")) {
          toast.error("❌ Unsupported file format");
        } else {
          toast.error(`⚠️ ${message}`);
        }
      } else {
        toast.error("⚠️ Something went wrong while uploading");
      }
    },
  });

  // ==================== UI COMPONENTS ====================

  /**
   * Loading card component shown during upload process
   * Displays spinner and current upload step
   *
   * @param fileName - Optional filename for tracking
   * @param step - Current upload step to display
   */
  const UploadingCard = ({
    fileName,
    step,
  }: {
    fileName?: string;
    step?: string;
  }) => (
    <div
      style={cardDimensions}
      className="relative bg-[#F8F8F8] border border-[#EAECF0] rounded-[8px] overflow-hidden flex flex-col justify-center items-center animate-fade-in"
    >
      {/* Spinning loader */}
      <div className="relative w-8 h-8 mb-2">
        <div className="absolute inset-0 rounded-full border-4 border-gray-300 animate-spin border-t-[#17457D]" />
      </div>
      {/* Upload progress text */}
      <p className="text-xs text-center text-[#667085] animate-pulse px-2">
        {step || "Uploading..."}
      </p>
    </div>
  );

  /**
   * Individual file card component
   * Shows different states: uploading, error, or completed video
   *
   * @param file - File details to display
   * @param index - Index in files array for removal
   */
  const FileCard = ({ file, index }: { file: FileDetails; index: number }) => {
    const isUploading = uploadSteps[file.name];
    const hasError = errors[file.name];

    // Show loading state during upload
    if (isUploading) {
      return (
        <UploadingCard fileName={file.name} step={uploadSteps[file.name]} />
      );
    }

    return (
      <div
        className="relative bg-[#F8F8F8] border border-[#EAECF0] rounded-[8px] overflow-hidden flex justify-center items-center group"
        style={cardDimensions}
      >
        {hasError ? (
          // Error state - show error message and remove button
          <div className="flex flex-col items-center justify-center p-2 text-center">
            <p className="text-xs text-red-500 mb-1">Upload failed</p>
            <button
              onClick={() => handleRemoveFile(index)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Remove
            </button>
          </div>
        ) : (
          // Success state - show video player with controls
          <>
            <VideoPlayerV3
              url={file.live_url as string}
              thumbnailUrl={file.thumbnail as string}
              playing={playingVideoUrl === file.live_url}
              muted={false}
              blurhash={file?.hash ? file.hash : (DEFAULT_BLURHASH as string)}
              onPlay={() => handlePlayVideo(file.live_url as string)}
              border="rounded-lg"
            />
            {/* Delete button - only visible on hover */}
            <button
              className="absolute top-2 right-2 bg-white border border-gray-200 rounded-full p-1 shadow hover:bg-gray-100 transition z-[999]"
              onClick={() => handleRemoveFile(index)}
              aria-label="Remove file"
            >
              <X size={12} />
            </button>
          </>
        )}
      </div>
    );
  };

  // ==================== RENDER ====================

  return (
    <div className="flex gap-y-2 gap-x-4 items-start flex-wrap">
      {/* 
        Upload Dropzone 
        Only shown when there's space for more files or when not showing selected files
      */}
      {(files.length < maxFiles || !showSelectedFiles) && (
        <div
          {...getRootProps()}
          className={`bg-[#F8F8F8] relative size-[124px] border border-dashed border-[#D0D5DD] rounded-[8px] text-center py-3 flex flex-col justify-center items-center cursor-pointer ${cardDesignSize}`}
        >
          {/* Hidden file input */}
          <input
            {...getInputProps()}
            className="w-full absolute top-0 left-0 h-full opacity-0"
          />
          {/* Upload icon */}
          <PlusIcon className="text-[#D0D5DD] h-[28px]" />
          {/* Upload text */}
          <p className="text-xs font-semibold text-[#D0D5DD]">Upload Media</p>
        </div>
      )}

      {/* 
        File Previews Grid
        Shows all uploaded files with their current state (loading, error, or success)
      */}
      {showSelectedFiles && !showUploadAnimationOnly && (
        <>
          {files.map((file, index) => (
            <FileCard key={`${file.name}-${index}`} file={file} index={index} />
          ))}
        </>
      )}

      {/* 
        Upload Animation Only Mode
        Shows only a loading card when showUploadAnimationOnly is true
      */}
      {uploading && showUploadAnimationOnly && <UploadingCard />}
    </div>
  );
};

export default VideoUploaderWithPreview;
